import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../utils/AuthContext";
import {
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase/config";
import { updateProfileName } from "../../utils/Api";

import PrimaryButton from "../../components/Buttons/PrimaryButton";
import SecondaryButton from "../../components/Buttons/SecondaryButton";
import UserNavbar from "../../components/UserNavbar";
import Footer from "../../components/Footer";

import { ArrowRight } from "lucide-react";

const Profile: React.FC = () => {
  const { user } = useAuth();
  const isPsdiUser = user?.uid === "keycloak-user";

  const [newName, setNewName] = useState("");
  const [nameError, setNameError] = useState("");
  const [nameSuccess, setNameSuccess] = useState("");
  const [nameLoading, setNameLoading] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);

  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);

  const handleNameChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setNameError("");
    setNameSuccess("");

    if (!user) {
      setNameError("User not authenticated");
      return;
    }

    if (!newName.trim()) {
      setNameError("Please enter a name");
      return;
    }

    setNameLoading(true);

    try {
      // Postgres is the source of truth for the name. Persist via the API
      // and store the fresh token it returns so the JWT's name claim stays in sync.
      const trimmed = newName.trim();
      const res = await updateProfileName(trimmed);
      const token = res.data?.access_token;
      if (token) {
        localStorage.setItem("access_token", token);
      }

      // Dual-write to Firestore for the transition window; best-effort, must not
      // fail the update now that the API call has succeeded (to be removed once
      // the backfill is verified in prod).
      try {
        await updateDoc(doc(db, "users", user.uid), { name: trimmed });
      } catch (firestoreError) {
        console.error("Error mirroring name to Firestore:", firestoreError);
      }

      setNameSuccess("Name updated successfully!");
      setNewName("");

      setTimeout(() => {
        setNameModalOpen(false);
        setNameSuccess("");
        window.location.reload();
      }, 1500);
    } catch (error: any) {
      console.error("Error updating name:", error);
      setNameError(error.message || "Failed to update name");
    } finally {
      setNameLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    if (!user || !user.email) {
      setPasswordError("User not authenticated");
      return;
    }

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError("Please fill in all fields");
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError("New password must be at least 6 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }

    setPasswordLoading(true);

    try {
      const credential = EmailAuthProvider.credential(
        user.email,
        currentPassword
      );
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);

      setPasswordSuccess("Password updated successfully!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");

      setTimeout(() => {
        setPasswordModalOpen(false);
        setPasswordSuccess("");
      }, 1500);
    } catch (error: any) {
      console.error("Error updating password:", error);
      if (error.code === "auth/invalid-credential") {
        setPasswordError("Current password is incorrect");
      } else if (error.code === "auth/weak-password") {
        setPasswordError("Password is too weak");
      } else if (error.code === "auth/requires-recent-login") {
        setPasswordError(
          "Please log out and log back in before changing your password"
        );
      } else {
        setPasswordError(error.message || "Failed to update password");
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="w-1/2 max-[600px]:w-full max-[600px]:px-4 mx-auto mt-16 mb-10 text-left">
        <h3 className="text-xl font-semibold tracking-tight text-gray-900">Profile</h3>
        <p className="text-base text-gray-500 mt-0.5">Loading...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <UserNavbar />
      <div className="flex-1 w-1/2 max-[600px]:w-full max-[600px]:px-4 mx-auto mt-16 mb-20 text-left">
        <h3 className="text-xl font-semibold tracking-tight text-gray-900">Profile</h3>
        <p className="text-base text-gray-500 mt-0.5">Manage your profile settings</p>

        {/* account details */}
        <div className="mt-8">
          <p className="text-xs font-medium uppercase tracking-widest text-gray-400 mb-3">Account details</p>
          <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
            <div className="flex items-center justify-between px-5 py-4 max-[600px]:flex-col max-[600px]:items-start max-[600px]:gap-1">
              <span className="text-sm text-gray-500">Name</span>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-900">{user.name || "—"}</span>
                {!isPsdiUser && (
                  <button
                    className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                    onClick={() => setNameModalOpen(true)}
                  >
                    <ArrowRight size={15} />
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between px-5 py-4 max-[600px]:flex-col max-[600px]:items-start max-[600px]:gap-1">
              <span className="text-sm text-gray-500">Email</span>
              <span className="text-sm text-gray-900 max-[600px]:break-all">{user.email}</span>
            </div>
          </div>
        </div>

        {/* security */}
        {!isPsdiUser && (
          <div className="mt-8">
            <p className="text-xs font-medium uppercase tracking-widest text-gray-400 mb-3">Security</p>
            <div className="border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between px-5 py-4 max-[600px]:flex-col max-[600px]:items-start max-[600px]:gap-3">
                <div>
                  <span className="text-sm font-medium text-gray-900">Password</span>
                  <p className="text-sm text-gray-400 mt-0.5">
                    Update your password to keep your account secure
                  </p>
                </div>
                <button
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors max-[600px]:w-full"
                  onClick={() => setPasswordModalOpen(true)}
                >
                  Change password
                </button>
              </div>
            </div>
          </div>
        )}

        {/* legal */}
        <div className="mt-8">
          <p className="text-xs font-medium uppercase tracking-widest text-gray-400 mb-3">Legal</p>
          <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
            <div className="flex items-center justify-between px-5 py-4 max-[600px]:flex-col max-[600px]:items-start max-[600px]:gap-1">
              <span className="text-sm text-gray-900">Terms of Service</span>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-400">Updated 23/07/2025</span>
                <Link to="/legal/terms-of-service">
                  <button className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors">
                    <ArrowRight size={15} />
                  </button>
                </Link>
              </div>
            </div>
            <div className="flex items-center justify-between px-5 py-4 max-[600px]:flex-col max-[600px]:items-start max-[600px]:gap-1">
              <span className="text-sm text-gray-900">Privacy Policy</span>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-400">Updated 23/07/2025</span>
                <Link to="/legal/privacy-policy">
                  <button className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors">
                    <ArrowRight size={15} />
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* change name modal */}
        {nameModalOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 max-[600px]:px-4">
            <div className="bg-white border border-gray-200 rounded-lg w-full max-w-md mt-[120px] max-[600px]:mt-[80px]">
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <h5 className="text-base font-semibold tracking-tight text-gray-900">Change name</h5>
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  onClick={() => setNameModalOpen(false)}
                >
                  <span className="text-xl leading-none">&times;</span>
                </button>
              </div>
              <form onSubmit={handleNameChange}>
                <div className="px-6 py-5 space-y-4">
                  <div>
                    <label htmlFor="newName" className="block text-sm font-medium text-gray-700 mb-1.5">
                      New name
                    </label>
                    <input
                      type="text"
                      className="w-full h-10 border border-gray-200 rounded-lg px-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors"
                      id="newName"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder="Enter your name"
                      maxLength={255}
                    />
                  </div>
                  {nameError && (
                    <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{nameError}</p>
                  )}
                  {nameSuccess && (
                    <p className="text-sm text-green-600 bg-green-50 border border-green-200 rounded-lg px-3 py-2">{nameSuccess}</p>
                  )}
                </div>
                <div className="flex justify-end gap-2 border-t border-gray-100 px-6 py-4">
                  <SecondaryButton type="button" onClick={() => setNameModalOpen(false)}>
                    Cancel
                  </SecondaryButton>
                  <PrimaryButton type="submit" disabled={nameLoading}>
                    {nameLoading ? "Updating..." : "Update name"}
                  </PrimaryButton>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* change password modal */}
        {passwordModalOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 max-[600px]:px-4">
            <div className="bg-white border border-gray-200 rounded-lg w-full max-w-md mt-[120px] max-[600px]:mt-[80px]">
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <h5 className="text-base font-semibold tracking-tight text-gray-900">Change password</h5>
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  onClick={() => setPasswordModalOpen(false)}
                >
                  <span className="text-xl leading-none">&times;</span>
                </button>
              </div>
              <form onSubmit={handlePasswordChange}>
                <div className="px-6 py-5 space-y-4">
                  <div>
                    <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Current password
                    </label>
                    <input
                      type="password"
                      className="w-full h-10 border border-gray-200 rounded-lg px-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors"
                      id="currentPassword"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                    />
                  </div>
                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1.5">
                      New password
                    </label>
                    <input
                      type="password"
                      className="w-full h-10 border border-gray-200 rounded-lg px-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors"
                      id="newPassword"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                    />
                  </div>
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Confirm new password
                    </label>
                    <input
                      type="password"
                      className="w-full h-10 border border-gray-200 rounded-lg px-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors"
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                    />
                  </div>
                  {passwordError && (
                    <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{passwordError}</p>
                  )}
                  {passwordSuccess && (
                    <p className="text-sm text-green-600 bg-green-50 border border-green-200 rounded-lg px-3 py-2">{passwordSuccess}</p>
                  )}
                </div>
                <div className="flex justify-end gap-2 border-t border-gray-100 px-6 py-4">
                  <SecondaryButton type="button" onClick={() => setPasswordModalOpen(false)}>
                    Cancel
                  </SecondaryButton>
                  <PrimaryButton type="submit" disabled={passwordLoading}>
                    {passwordLoading ? "Updating..." : "Update password"}
                  </PrimaryButton>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Profile;
